/* La fiche d'une référence : ce qu'elle est, ce qu'elle coûte, ce qu'elle
   rapporte, et à qui elle a été vendue.
 *
 * Trois prix cohabitent et ne se voient pas de la même place : ce que l'usine
 * demande (direction seule), le plancher de chaque agent, et ce que chaque
 * client paie. La fiche les montre dans cet ordre, en commençant par ce que
 * celui qui regarde a le droit de voir. */

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAgents, useClients, useDocumentsProduit, useLignesProduit, usePrixAgent, usePrixClient,
  usePrixUsine, useProduit, useUsine,
} from "@/lib/donnees";
import { autorise, useNiveau, useSession } from "@/lib/session";
import { Empty, Kpi, Kpis, Logo, Onglets, Section, Tag } from "@/ui/primitives";
import { Identite } from "@/ui/Fiche";
import { Icon } from "@/ui/Icon";
import { Table, dansUnChamp } from "@/ui/Table";
import { dateFr, entier, euro, euroCourt, poids, pourcent } from "@/lib/format";
import { GROUPES_PRODUIT, UNITES, libelleCourt } from "@/domain/fiches";
import { enVigueur, historique, margeDe } from "@/domain/prix";
import { nomNature } from "@/domain/documents-usine";
import { etapeDe } from "@/domain/ventes";
import { Journal } from "@/features/journal/Journal";
import { enregistrerFiche } from "@/features/journal/actions";
import { lienDocument } from "@/features/usines/actions";
import type { Produit } from "@/types/metier";

type Onglet = "fiche" | "prix" | "ventes" | "documents" | "suivi";

export function FicheProduit() {
  const { id } = useParams<{ id: string }>();
  const navigue = useNavigate();
  const niveau = useNiveau();
  const { profil } = useSession();
  const voitUsine = autorise(niveau, "agent");
  const voitPrixUsine = autorise(niveau, "admin");
  const modifiable = autorise(niveau, "agent");

  const { donnees: produit, chargement, erreur, recharger } = useProduit(id);
  const { donnees: usine } = useUsine(produit?.usine_id);
  const { donnees: lignes } = useLignesProduit(id);
  const { donnees: documents } = useDocumentsProduit(id);
  const { donnees: clients } = useClients();
  const { donnees: agents } = useAgents();
  const { donnees: prixUsine } = usePrixUsine(true);
  const { donnees: prixAgent } = usePrixAgent(true);
  const { donnees: prixClient } = usePrixClient(true);
  const [onglet, setOnglet] = useState<Onglet>("fiche");

  const nomClient = useMemo(() => new Map(clients.map((c) => [c.id, c.nom] as const)), [clients]);
  const nomAgent = useMemo(() => new Map(agents.map((a) => [a.id, a.nom] as const)), [agents]);

  const sesUsine = useMemo(() => prixUsine.filter((p) => p.produit_id === id), [prixUsine, id]);
  const sesAgent = useMemo(() => prixAgent.filter((p) => p.produit_id === id), [prixAgent, id]);
  const sesClient = useMemo(() => prixClient.filter((p) => p.produit_id === id), [prixClient, id]);

  const coutVif = useMemo(() => enVigueur(sesUsine)[0] ?? null, [sesUsine]);
  const planchersVifs = useMemo(() => enVigueur(sesAgent), [sesAgent]);
  const tarifsVifs = useMemo(() => enVigueur(sesClient), [sesClient]);
  /* Les trois archives dans un même tableau : on les ramène à une forme
     commune plutôt que de les trier par type à l'affichage. */
  const archives = useMemo(() => {
    type Archive = {
      id: string; quoi: string; qui: string; montant: number;
      debut_le: string; fin_le: string | null; motif: string | null;
    };
    const lignes: Archive[] = [];
    for (const p of sesUsine) {
      if (!p.fin_le) continue;
      lignes.push({ id: p.id, quoi: "Prix d'usine", qui: "l'usine", montant: Number(p.prix),
        debut_le: p.debut_le, fin_le: p.fin_le, motif: p.motif });
    }
    for (const p of sesAgent) {
      if (!p.fin_le) continue;
      lignes.push({ id: p.id, quoi: "Plancher agent", qui: nomAgent.get(p.agent_id) ?? "—",
        montant: Number(p.prix_mini), debut_le: p.debut_le, fin_le: p.fin_le, motif: p.motif });
    }
    for (const p of sesClient) {
      if (!p.fin_le) continue;
      lignes.push({ id: p.id, quoi: "Tarif client", qui: nomClient.get(p.client_id) ?? "—",
        montant: Number(p.prix), debut_le: p.debut_le, fin_le: p.fin_le, motif: p.motif });
    }
    return lignes.sort((a, b) => b.fin_le!.localeCompare(a.fin_le!));
  }, [sesUsine, sesAgent, sesClient, nomAgent, nomClient]);

  /* Ce qui a été vendu de cette référence : quantité, chiffre, prix moyen. */
  const vendu = useMemo(() => {
    const qte = lignes.reduce((s, l) => s + Number(l.qte), 0);
    const total = lignes.reduce((s, l) => s + Number(l.total), 0);
    return { qte, total, moyen: qte > 0 ? total / qte : 0 };
  }, [lignes]);

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;
  if (chargement) return <div className="pad"><div className="chargement">Chargement…</div></div>;
  if (!produit) {
    return (
      <div className="pad">
        <Empty titre="Référence introuvable" detail="Choisissez une référence dans le catalogue." />
      </div>
    );
  }

  const enregistrer = async (champs: Partial<Produit>) => {
    if (!profil) throw new Error("Profil inconnu : reconnectez-vous.");
    await enregistrerFiche<Produit>(
      { sujet: "produits", sujetId: produit.id, auteur: profil.id },
      "produits",
      produit as unknown as Record<string, unknown>,
      champs as Record<string, unknown>,
    );
    recharger();
  };

  const prixCatalogue = produit.prix == null ? null : Number(produit.prix);
  const cout = coutVif ? Number(coutVif.prix) : null;
  const marge = margeDe(prixCatalogue, cout);

  const onglets: { cle: Onglet; nom: string; n?: number }[] = [
    { cle: "fiche", nom: "Fiche" },
    { cle: "prix", nom: "Prix", n: planchersVifs.length + tarifsVifs.length },
    { cle: "ventes", nom: "Ventes", n: lignes.length },
    { cle: "documents", nom: "Documents", n: documents.length },
    { cle: "suivi", nom: "Suivi" },
  ];

  return (
    <div className="pad">
      <div className="fiche-tete">
        {voitUsine && usine ? (
          <Logo src={usine.logo_url} nom={usine.nom} couleur={usine.couleur} taille={44} />
        ) : null}
        <div className="fiche-t">
          <div className="section-t">{produit.nom}</div>
          <div className="section-n">
            <span className="mono">{produit.ref}</span>
            {" · "}
            {[
              produit.gamme,
              produit.conditionnement,
              produit.origine,
              voitUsine && usine ? usine.nom : null,
            ].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="fiche-a fiche-a-centre">
          {produit.actif ? <Tag ton="ok" dot>au catalogue</Tag> : <Tag dot>retirée</Tag>}
          <button type="button" className="btn" onClick={() => navigue("/catalogue")}>
            <Icon n="retour" cls="" />
            Le catalogue
          </button>
        </div>
      </div>

      <div className="fiche-parcours">
        <Onglets items={onglets} actif={onglet} onChange={setOnglet} />
      </div>

      {onglet === "fiche" ? (
        <>
          <Kpis>
            <Kpi
              k="Prix de vente"
              v={prixCatalogue == null ? "—" : euro(prixCatalogue)}
              n={produit.unite ? `par ${libelleCourt(UNITES, produit.unite).toLowerCase()}` : "au catalogue"}
            />
            {voitPrixUsine ? (
              <Kpi
                k="Prix d'usine"
                v={cout == null ? "—" : euro(cout)}
                n={marge == null ? "aucune marge calculable" : `marge ${pourcent(marge)}`}
              />
            ) : (
              <Kpi k="Planchers posés" v={entier(planchersVifs.length)} n="par agent" />
            )}
            <Kpi
              k="Vendu"
              v={entier(vendu.qte)}
              n={`${lignes.length} lignes · ${euroCourt(vendu.total)}`}
            />
            <Kpi
              k="Prix moyen obtenu"
              v={vendu.moyen > 0 ? euro(vendu.moyen) : "—"}
              n={`${tarifsVifs.length} tarifs clients`}
            />
          </Kpis>

          <Identite
            valeur={produit}
            groupes={GROUPES_PRODUIT}
            modifiable={modifiable}
            enregistrer={enregistrer}
          />

          <Section titre="Logistique" note="ce qui se calcule, et qu'on ne saisit pas">
            <div className="card card-pad">
              <dl className="kv">
                <dt>Poids d'un carton</dt>
                <dd className="mono">
                  {produit.poids_brut != null && produit.par_carton != null
                    ? `${(Number(produit.poids_brut) * Number(produit.par_carton)).toFixed(2).replace(".", ",")} kg`
                    : "—"}
                </dd>
                <dt>Unités par palette</dt>
                <dd className="mono">
                  {produit.par_carton != null && produit.par_palette != null
                    ? entier(Number(produit.par_carton) * Number(produit.par_palette))
                    : "—"}
                </dd>
                <dt>Tare au carton</dt>
                <dd className="mono">
                  {produit.poids_brut != null && produit.poids_net != null
                    ? `${(Number(produit.poids_brut) - Number(produit.poids_net)).toFixed(3).replace(".", ",")} kg`
                    : "—"}
                </dd>
                <dt>Usine</dt>
                <dd>
                  {voitUsine && usine ? (
                    <button
                      type="button"
                      className="piece-btn"
                      onClick={() => navigue(`/usines/${usine.id}`)}
                    >
                      <span className="mono">{usine.nom}</span>
                    </button>
                  ) : (
                    "—"
                  )}
                </dd>
              </dl>
            </div>
          </Section>
        </>
      ) : null}

      {onglet === "prix" ? (
        <>
          {voitPrixUsine ? (
            <Section titre="Prix d'usine" note="ce qu'elle demande — direction seule">
              {sesUsine.length ? (
                <Table
                  lignes={historique(sesUsine)}
                  cle={(p) => p.id}
                  colonnes={[
                    { cle: "prix", titre: "Prix", type: "euro", nombre: (p) => Number(p.prix),
                      sous: (p) => (p.fin_le ? "clos" : "en vigueur") },
                    { cle: "debut_le", titre: "Du", type: "date" },
                    { cle: "fin_le", titre: "Au", type: "date", mobile: false,
                      texte: (p) => (p.fin_le ? dateFr(p.fin_le) : "—") },
                    { cle: "motif", titre: "Motif", mobile: false, texte: (p) => p.motif ?? "—" },
                  ]}
                />
              ) : (
                <Empty titre="Aucun prix d'usine" detail="Il s'importe ou se pose depuis l'écran Prix." />
              )}
            </Section>
          ) : null}

          <Section titre="Planchers par agent" note="le minimum auquel chacun peut vendre">
            {planchersVifs.length ? (
              <Table
                lignes={planchersVifs}
                cle={(p) => p.id}
                colonnes={[
                  { cle: "agent_id", titre: "Agent", filtre: "texte",
                    texte: (p) => nomAgent.get(p.agent_id) ?? "—",
                    sous: (p) => `depuis le ${dateFr(p.debut_le)}` },
                  { cle: "prix_mini", titre: "Plancher", type: "euro",
                    nombre: (p) => Number(p.prix_mini) },
                  voitPrixUsine && {
                    cle: "marge", titre: "Marge sur usine", court: "Marge", type: "pct",
                    nombre: (p) => margeDe(Number(p.prix_mini), cout) ?? 0,
                  },
                  { cle: "debut_le", titre: "Depuis", type: "date", mobile: false },
                ]}
              />
            ) : (
              <Empty
                titre="Aucun plancher propre"
                detail="À défaut, le prix du catalogue fait plancher pour tout le monde."
              />
            )}
          </Section>

          <Section titre="Tarifs clients" note="ce que chacun paie aujourd'hui">
            {tarifsVifs.length ? (
              <Table
                lignes={tarifsVifs}
                cle={(p) => p.id}
                onClic={(p, ev) => {
                  if (!dansUnChamp(ev)) navigue(`/clients/${p.client_id}`);
                }}
                colonnes={[
                  { cle: "client_id", titre: "Client", filtre: "texte",
                    texte: (p) => nomClient.get(p.client_id) ?? "—",
                    sous: (p) => `depuis le ${dateFr(p.debut_le)}` },
                  { cle: "prix", titre: "Prix convenu", court: "Prix", type: "euro",
                    nombre: (p) => Number(p.prix) },
                  { cle: "debut_le", titre: "Depuis", type: "date", mobile: false },
                  { cle: "motif", titre: "Motif", mobile: false, texte: (p) => p.motif ?? "—" },
                ]}
              />
            ) : (
              <Empty titre="Aucun tarif client" detail="Chacun paie le prix du catalogue." />
            )}
          </Section>

          {archives.length ? (
            <Section titre="Archives" note={`${archives.length} périodes closes`}>
              <Table
                lignes={archives}
                cle={(p) => p.id}
                colonnes={[
                  { cle: "quoi", titre: "Prix", filtre: "select", sous: (p) => p.qui },
                  { cle: "qui", titre: "Pour", mobile: false },
                  { cle: "montant", titre: "Montant", type: "euro", nombre: (p) => p.montant },
                  { cle: "debut_le", titre: "Du", type: "date" },
                  { cle: "fin_le", titre: "Au", type: "date", mobile: false,
                    texte: (p) => (p.fin_le ? dateFr(p.fin_le) : "—") },
                  { cle: "motif", titre: "Motif", mobile: false, texte: (p) => p.motif ?? "—" },
                ]}
              />
            </Section>
          ) : null}
        </>
      ) : null}

      {onglet === "ventes" ? (
        <Section titre="Vendu" note={`${lignes.length} lignes de vente`}>
          {lignes.length ? (
            <Table
              lignes={lignes}
              cle={(l) => l.id}
              chercher="Document"
              onClic={(l, ev) => {
                if (!dansUnChamp(ev) && l.vente) navigue(`/ventes/${l.vente.id}`);
              }}
              colonnes={[
                { cle: "document", titre: "Document", type: "ref", filtre: "texte",
                  texte: (l) => l.vente?.reference ?? "—",
                  sous: (l) => (l.vente ? etapeDe(l.vente.etape).court : "") },
                { cle: "client", titre: "Client", filtre: "select", mobile: false,
                  texte: (l) =>
                    l.vente?.client_id ? (nomClient.get(l.vente.client_id) ?? "—") : "—" },
                { cle: "qte", titre: "Quantité", court: "Qté", type: "num", total: true,
                  nombre: (l) => Number(l.qte) },
                { cle: "prix", titre: "Prix unitaire", court: "PU", type: "euro",
                  agregat: "moyenne", total: true, totalTitre: "Prix moyen",
                  nombre: (l) => Number(l.prix) },
                { cle: "total", titre: "Total", type: "euro", total: true,
                  nombre: (l) => Number(l.total) },
                { cle: "date", titre: "Date", type: "date", mobile: false,
                  texte: (l) => (l.vente ? dateFr(l.vente.date_document) : "—") },
              ]}
            />
          ) : (
            <Empty titre="Jamais vendue" detail="Cette référence n'apparaît sur aucun document." />
          )}
        </Section>
      ) : null}

      {onglet === "documents" ? (
        <Section titre="Documents" note="fiches techniques et produits de l'usine">
          {documents.length ? (
            <Table
              lignes={documents}
              cle={(d) => d.id}
              onClic={(d, ev) => {
                if (dansUnChamp(ev)) return;
                void lienDocument(d.chemin).then((u) => {
                  if (u) window.open(u, "_blank", "noopener");
                });
              }}
              colonnes={[
                { cle: "titre", titre: "Document", type: "ref", filtre: "texte",
                  sous: (d) => d.nom },
                { cle: "nature", titre: "Nature", filtre: "select",
                  texte: (d) => nomNature(d.nature),
                  rendu: (d) => <Tag>{nomNature(d.nature)}</Tag> },
                { cle: "langue", titre: "Langue", mobile: false, texte: (d) => d.langue ?? "—" },
                { cle: "taille", titre: "Taille", mobile: false,
                  texte: (d) => poids(Number(d.taille)) },
                { cle: "cree_le", titre: "Déposé", type: "date", mobile: false },
              ]}
            />
          ) : (
            <Empty
              titre="Aucun document"
              detail="Les fiches techniques se déposent sur la fiche de l'usine, rattachées à cette référence."
            />
          )}
        </Section>
      ) : null}

      {onglet === "suivi" ? <Journal sujet="produits" sujetId={produit.id} /> : null}
    </div>
  );
}
